import { Events } from '@elyx-code/common-ts-utils';

export enum NotificationTypes {
	LogicUnreachable = 'logic-unreachable',
	MissingValue = 'missing-value',
	AbstractionNotImplemented = 'abstraction-not-implemented',
}

export enum NotificationSeverity {
	Error = 'error',
	Warning = 'warning',
	Info = 'info',
	Success = 'success',
}

export enum NotificationActionType {
	InternalNavigation = 'internal-navigation',
	ExternalNavigation = 'external-navigation',
	CanvasNavigation = 'canvas-navigation',
}

export interface INotificationAction {
	type: NotificationActionType;
	value: string;
}

export interface INotification {
	types: NotificationTypes[];
	severity: NotificationSeverity;
	title: string;
	message: string;
	data?: any;
	action?: INotificationAction;
}

export class Notification implements INotification {
	channel: NotificationsChannel;
	uuid: string;
	seen: boolean = false;
	types: NotificationTypes[];
	severity: NotificationSeverity;
	action?: INotificationAction;
	title: string;
	message: string;
	createdAt: string;
	data: any | null = null;

	constructor(
		payload: INotification,
		uuid: string,
		channel: NotificationsChannel,
	) {
		this.channel = channel;
		this.uuid = uuid;

		this.createdAt = new Date().toISOString();

		this.types = payload.types || [];
		this.severity = payload.severity || NotificationSeverity.Info;
		this.title = payload.title || '';
		this.message = payload.message || '';
		this.data = payload.data || null;
		this.action = payload.action || null;
	}

	static navigate = (url: string) => {
		// Native browser navigation by default
		window.location.href = url;
	};

	update(payload: Partial<INotification>): Notification {
		this.types = payload.types || this.types;
		this.severity = payload.severity || this.severity;
		this.title = payload.title || this.title;
		this.message = payload.message || this.message;
		this.data = payload.data || this.data;

		this.channel.emit('update', this);

		return this;
	}

	markAsSeen(): Notification {
		this.seen = true;

		this.channel.emit('seen', this.uuid);

		return this;
	}

	// Executes the action based on type
	executeAction() {
		if (!this.action) {
			return;
		}

		switch (this.action.type) {
			case NotificationActionType.InternalNavigation:
				Notification.navigate(this.action.value);
				break;
			case NotificationActionType.ExternalNavigation:
				window.open(this.action.value, '_blank');
				break;
			case NotificationActionType.CanvasNavigation:
				// @ts-ignore
				window.canvas?.scrollToObjectById(this.action.value, () => {
					// Zoom in on the target object in the canvas
					// @ts-ignore
					window.canvas?.zoomTo(1.1);
				});
				break;
			default:
				break;
		}
	}
}

export class NotificationsChannel extends Events {
	name: string;
	notifications: {
		[key: string]: Notification;
	} = {};
	store: Notifications;

	constructor(name: string, store: Notifications) {
		super();
		this.name = name;
		this.store = store;
	}

	push(notification: INotification, uuid: string): Notification {
		const existingNotification = this.get(uuid);

		if (existingNotification) {
			return existingNotification.update(notification);
		}

		const instance = new Notification(notification, uuid, this);

		this.notifications[uuid] = instance;

		this.emit('new', notification);
		this.store.emit('new', notification);

		return instance;
	}

	remove(uuid: string) {
		if (!this.notifications[uuid]) {
			return;
		}

		delete this.notifications[uuid];

		this.emit('remove', uuid);
		this.store.emit('remove', uuid);
	}

	get(uuid: string) {
		return this.notifications[uuid];
	}

	clear() {
		this.notifications = {};

		this.emit('clear');
		this.store.emit('clear-channel', this.name);

		this.destroy();
	}

	markAsSeen(notification: Notification) {
		notification.markAsSeen();
	}

	// -- Getters --

	getErrors() {
		return Object.values(this.notifications).filter(
			(notification) => notification.severity === NotificationSeverity.Error,
		);
	}

	getWarnings() {
		return Object.values(this.notifications).filter(
			(notification) => notification.severity === NotificationSeverity.Warning,
		);
	}

	getInfos() {
		return Object.values(this.notifications).filter(
			(notification) => notification.severity === NotificationSeverity.Info,
		);
	}

	getSuccess() {
		return Object.values(this.notifications).filter(
			(notification) => notification.severity === NotificationSeverity.Success,
		);
	}

	getUnseen() {
		return Object.values(this.notifications).filter(
			(notification) => !notification.seen,
		);
	}

	getUnseenErrors() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Error &&
				!notification.seen,
		);
	}

	getUnseenWarnings() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Warning &&
				!notification.seen,
		);
	}

	getUnseenInfos() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Info &&
				!notification.seen,
		);
	}

	getUnseenSuccess() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Success &&
				!notification.seen,
		);
	}

	getSeenErrors() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Error &&
				notification.seen,
		);
	}

	getSeenWarnings() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Warning &&
				notification.seen,
		);
	}

	getSeenInfos() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Info &&
				notification.seen,
		);
	}

	getSeenSuccess() {
		return Object.values(this.notifications).filter(
			(notification) =>
				notification.severity === NotificationSeverity.Success &&
				notification.seen,
		);
	}

	getSeen() {
		return Object.values(this.notifications).filter(
			(notification) => notification.seen,
		);
	}

	getUnseenCount() {
		return this.getUnseen().length;
	}

	getSeenCount() {
		return this.getSeen().length;
	}

	getErrorsCount() {
		return this.getErrors().length;
	}

	getWarningsCount() {
		return this.getWarnings().length;
	}

	getInfosCount() {
		return this.getInfos().length;
	}

	getSuccessCount() {
		return this.getSuccess().length;
	}

	getCount() {
		return Object.values(this.notifications).length;
	}

	getUnseenErrorsCount() {
		return this.getUnseenErrors().length;
	}

	getUnseenWarningsCount() {
		return this.getUnseenWarnings().length;
	}

	getUnseenInfosCount() {
		return this.getUnseenInfos().length;
	}

	getUnseenSuccessCount() {
		return this.getUnseenSuccess().length;
	}

	getSeenErrorsCount() {
		return this.getSeenErrors().length;
	}

	getSeenWarningsCount() {
		return this.getSeenWarnings().length;
	}

	getSeenInfosCount() {
		return this.getSeenInfos().length;
	}

	getSeenSuccessCount() {
		return this.getSeenSuccess().length;
	}

	hasErrors() {
		return this.getErrorsCount() > 0;
	}

	hasWarnings() {
		return this.getWarningsCount() > 0;
	}

	hasInfos() {
		return this.getInfosCount() > 0;
	}

	hasSuccess() {
		return this.getSuccessCount() > 0;
	}

	hasUnseen() {
		return this.getUnseenCount() > 0;
	}

	hasSeen() {
		return this.getSeenCount() > 0;
	}

	hasUnseenErrors() {
		return this.getUnseenErrorsCount() > 0;
	}

	hasUnseenWarnings() {
		return this.getUnseenWarningsCount() > 0;
	}

	hasUnseenInfos() {
		return this.getUnseenInfosCount() > 0;
	}

	hasUnseenSuccess() {
		return this.getUnseenSuccessCount() > 0;
	}

	hasSeenErrors() {
		return this.getSeenErrorsCount() > 0;
	}

	hasSeenWarnings() {
		return this.getSeenWarningsCount() > 0;
	}

	hasSeenInfos() {
		return this.getSeenInfosCount() > 0;
	}

	hasSeenSuccess() {
		return this.getSeenSuccessCount() > 0;
	}

	hasNotifications() {
		return this.getCount() > 0;
	}

	hasUnseenNotifications() {
		return this.getUnseenCount() > 0;
	}

	hasSeenNotifications() {
		return this.getSeenCount() > 0;
	}

	hasUnseenErrorsNotifications() {
		return this.getUnseenErrorsCount() > 0;
	}

	hasUnseenWarningsNotifications() {
		return this.getUnseenWarningsCount() > 0;
	}

	hasUnseenInfosNotifications() {
		return this.getUnseenInfosCount() > 0;
	}

	hasUnseenSuccessNotifications() {
		return this.getUnseenSuccessCount() > 0;
	}

	hasSeenErrorsNotifications() {
		return this.getSeenErrorsCount() > 0;
	}

	hasSeenWarningsNotifications() {
		return this.getSeenWarningsCount() > 0;
	}

	hasSeenInfosNotifications() {
		return this.getSeenInfosCount() > 0;
	}

	hasSeenSuccessNotifications() {
		return this.getSeenSuccessCount() > 0;
	}

	hasErrorsNotifications() {
		return this.getErrorsCount() > 0;
	}

	hasWarningsNotifications() {
		return this.getWarningsCount() > 0;
	}

	hasInfosNotifications() {
		return this.getInfosCount() > 0;
	}

	hasSuccessNotifications() {
		return this.getSuccessCount() > 0;
	}
}

export class Notifications extends Events {
	channels: {
		[key: string]: NotificationsChannel;
	} = {};

	push(
		channelName: string,
		notification: INotification,
		uuid: string,
	): Notification {
		const channel = this.getChannel(channelName);

		const existingNotification = channel.get(uuid);

		if (existingNotification) {
			return existingNotification.update(notification);
		}

		const instance = channel.push(notification, uuid);

		return instance;
	}

	remove(channelName: string, uuid: string) {
		const channel = this.getChannel(channelName);
		channel?.remove(uuid);
	}

	getChannel(name: string): NotificationsChannel {
		if (!this.channels[name]) {
			this.channels[name] = new NotificationsChannel(name, this);
		}

		return this.channels[name];
	}

	clearChannel(name: string) {
		const channel = this.getChannel(name);

		channel.clear();
	}

	removeChannel(channelName: string) {
		if (!this.channels[channelName]) {
			return;
		}

		this.channels[channelName].clear();

		delete this.channels[channelName];

		this.emit('remove-channel', channelName);
	}

	// Clear all channels
	clear() {
		Object.keys(this.channels).forEach((channelName) => {
			this.channels[channelName].clear();
		});

		this.channels = {};

		this.emit('clear');

		this.destroy();
	}
}

const notifications = new Notifications();

export default notifications;
